/**
 * Pure helpers for the hotel-checkout payment schedule:
 *  - `computePaymentSchedule` turns a rate's Sabre deposit policies into a
 *    structured list of `{ kind, amount, currency, percent?, dueDate? }`
 *    entries (no display strings).
 *  - `formatPaymentScheduleLabel` derives the user-facing label from one
 *    entry. Labels are NOT persisted on Firestore so copy can change in
 *    code without re-writing stored data.
 *
 * No React or Firebase deps — safe to import from both client and server.
 */

import type { PaymentScheduleEntry } from './models/schemas/itinerary-item-schemas.js';
import type { CancellationPolicy, DepositPolicy } from './models/schemas/room-schemas.js';

const ROUND_CENTS = 100;

export interface ComputePaymentScheduleInput {
  /** Final amount the user agreed to pay for this room. */
  total: { amount: number; currency: string };
  /** Rate-level flag — when true the full amount is charged at booking. */
  prePaymentRequired?: boolean;
  /**
   * Rate-level flag — Sabre's charge-at-booking signal (`Guarantee.GuaranteeType === 'DEP'`, see
   * `sabre/service.ts`). When true the hotel takes the deposit at booking even when Sabre sends no
   * structured `DepositPolicies` to quantify it — that absent-policies shape is the NORMAL one for
   * DEP rates (ELARA-879). When false (and no policies) the full amount is due at the property.
   */
  depositRequired?: boolean;
  /**
   * Rate-level flag — when explicitly `false` the rate is non-refundable, i.e. the amount is
   * committed at booking even with no deposit/prepay policy. `undefined` means unknown (treated as
   * the safe refundable/pay-at-property case).
   */
  refundable?: boolean;
  depositPolicies?: DepositPolicy[];
  /** ISO YYYY-MM-DD; required for resolving `BeforeArrival` / `AfterArrival` offsets. */
  checkInDate: string;
  /** ISO YYYY-MM-DD; required for resolving `AfterDeparture` offsets. */
  checkOutDate: string;
  /**
   * Override for "today" — pass a fixed value in tests to make output stable.
   * Defaults to `new Date()` at call time.
   */
  now?: Date;
}

/** Computes the structured payment schedule for one room's rate. */
export function computePaymentSchedule(input: ComputePaymentScheduleInput): PaymentScheduleEntry[] {
  const { total, prePaymentRequired, depositRequired, refundable, depositPolicies, checkInDate, checkOutDate } = input;
  const now = input.now ?? new Date();
  const today = startOfDay(now);
  const currency = total.currency;

  // 1. Full prepayment — entire amount due at booking.
  if (prePaymentRequired) {
    return [
      {
        kind: 'at_booking',
        amount: roundCurrency(total.amount),
        currency,
        percent: 100,
      },
    ];
  }

  // 2. No structured deposit/prepay policy. Two shapes are still charged at booking:
  //    - a NON-REFUNDABLE rate is committed up front (the amount is owed regardless of the stay);
  //    - a DEP-guarantee rate (`depositRequired`) — the hotel charges the deposit at booking even
  //      though Sabre sends no structured DepositPolicies to quantify it, which is the NORMAL DEP
  //      shape. Refundability governs refunds, not charge timing: a refundable DEP rate is charged
  //      now and refunded on a timely cancel (ELARA-879 — these read as "due at the property" and
  //      every money surface told the traveler "no charge today" while the hotel charged the card).
  //    Only a rate with neither signal is a pay-at-property balance.
  if (depositRequired === false || !depositPolicies || depositPolicies.length === 0) {
    const chargedAtBooking = refundable === false || depositRequired === true;
    return [
      {
        kind: chargedAtBooking ? 'at_booking' : 'balance',
        amount: roundCurrency(total.amount),
        currency,
        ...(chargedAtBooking ? { percent: 100 } : {}),
      },
    ];
  }

  // 3. One or more structured deposit policies — resolve each into an entry,
  //    then back-fill the remaining balance as a final entry.
  const partial: Array<PaymentScheduleEntry & { _sortKey: number }> = [];
  for (const policy of depositPolicies) {
    const amount = resolvePolicyAmount(policy, total.amount);
    if (amount === undefined || amount <= 0) continue;

    const resolved = resolvePolicyDate({ deadline: policy.deadline, checkInDate, checkOutDate });
    const isAtBooking = resolved.dueDate === undefined || resolved.dueDate <= today;
    const dueDateIso = resolved.dueDate ? toIsoDate(resolved.dueDate) : undefined;

    partial.push({
      kind: isAtBooking ? 'at_booking' : 'balance',
      amount: roundCurrency(amount),
      currency: policy.amountPercent?.currency ?? currency,
      percent: policy.amountPercent?.percent,
      dueDate: dueDateIso,
      _sortKey: resolved.dueDate ? resolved.dueDate.getTime() : 0,
    });
  }

  // Sort + merge entries that resolve to the same date.
  partial.sort((a, b) => a._sortKey - b._sortKey);
  const merged: Array<PaymentScheduleEntry & { _sortKey: number }> = [];
  for (const entry of partial) {
    const prev = merged.at(-1);
    if (prev && prev.dueDate === entry.dueDate && prev.kind === entry.kind) {
      prev.amount = roundCurrency(prev.amount + entry.amount);
      prev.percent =
        prev.percent !== undefined && entry.percent !== undefined ? prev.percent + entry.percent : undefined;
    } else {
      merged.push({ ...entry });
    }
  }

  const result: PaymentScheduleEntry[] = merged.map(({ _sortKey: _ignored, ...rest }) => rest);

  // Back-fill the remainder as a balance line so the entries sum to `total`.
  const collected = result.reduce((sum, e) => sum + e.amount, 0);
  const remainder = roundCurrency(total.amount - collected);
  if (remainder > 0) {
    result.push({
      kind: 'balance',
      amount: remainder,
      currency,
    });
  }

  return result;
}

/**
 * Folds N per-room schedules into one trip-level schedule. Always aggregates
 * — when rooms have differently-shaped policies (one prepay, one 25% deposit)
 * the result reads as the actual cashflow: a single "due at booking" line
 * plus a line per future date plus a final "due at the property" line. Rooms
 * with shape (X, due May 1) sum together; an at-booking line and a "due May
 * 1" line stay separate because the user is paying on different dates.
 *
 * Assumes a single currency across all rooms (callers guard on mixed
 * currencies upstream). Owns the sort/ordering convention so client (live
 * checkout preview) and server (persisted combined schedule) stay identical.
 */
export function combinePaymentSchedules(
  schedules: Array<PaymentScheduleEntry[] | undefined>,
): PaymentScheduleEntry[] {
  const flat: PaymentScheduleEntry[] = [];
  for (const schedule of schedules) {
    if (schedule) flat.push(...schedule);
  }
  if (flat.length === 0) return [];
  const currency = flat[0]!.currency;

  // Bucket by date when present, else by kind. Entries without a `dueDate`
  // collapse into one of two buckets — "at_booking" (immediate) or "balance"
  // (due at the property) — so we don't fragment by room.
  const bucketKey = (e: PaymentScheduleEntry): string =>
    e.dueDate ? `date:${e.dueDate}` : `kind:${e.kind}`;

  const buckets = new Map<string, { kind: PaymentScheduleEntry['kind']; dueDate?: string; total: number }>();
  for (const entry of flat) {
    const key = bucketKey(entry);
    const existing = buckets.get(key);
    if (existing) {
      existing.total += entry.amount;
    } else {
      buckets.set(key, { kind: entry.kind, dueDate: entry.dueDate, total: entry.amount });
    }
  }

  return [...buckets.values()]
    .toSorted(sortBuckets)
    .map((b) => ({
      kind: b.kind,
      amount: roundCurrency(b.total),
      currency,
      ...(b.dueDate ? { dueDate: b.dueDate } : {}),
    }));
}

/**
 * Display order: immediate at-booking first, then dated entries ascending,
 * then "due at the property" balance last. Ensures the user reads top-to-bottom
 * in cashflow order.
 */
function sortBuckets(
  a: { kind: PaymentScheduleEntry['kind']; dueDate?: string },
  b: { kind: PaymentScheduleEntry['kind']; dueDate?: string },
): number {
  const order = (x: typeof a): number => {
    if (x.kind === 'at_booking' && !x.dueDate) return 0;
    if (x.dueDate) return 1;
    return 2; // balance, no date — "due at the property"
  };
  const oa = order(a);
  const ob = order(b);
  if (oa !== ob) return oa - ob;
  return (a.dueDate ?? '').localeCompare(b.dueDate ?? '');
}

/**
 * Splits a (combined) schedule into what is due NOW — the `at_booking` total the card is charged on
 * submit — vs what is due LATER (dated deposits + the at-property balance). Drives the checkout's
 * "you will / won't be charged today" disclosure and whether a CVV must be captured. A booking with a
 * non-zero `dueNow` charges at submit and needs a CVV.
 */
export function summarizePaymentSchedule(entries: PaymentScheduleEntry[]): {
  dueNow?: { amount: number; currency: string };
  dueLater?: { amount: number; currency: string };
} {
  if (entries.length === 0) return {};
  const currency = entries[0]!.currency;
  let now = 0;
  let later = 0;
  for (const entry of entries) {
    if (entry.kind === 'at_booking') now += entry.amount;
    else later += entry.amount;
  }
  return {
    ...(now > 0 ? { dueNow: { amount: roundCurrency(now), currency } } : {}),
    ...(later > 0 ? { dueLater: { amount: roundCurrency(later), currency } } : {}),
  };
}

export interface CancellationDeadline {
  /** True when any cancellation policy allows a refund (there is a free-cancellation window). */
  refundable: boolean;
  /** The latest date a free cancellation is allowed, when a refundable policy carries a resolvable deadline. */
  freeUntil?: Date;
}

/**
 * Resolve the free-cancellation cutoff from the structured cancellation policy frozen onto the booking at
 * submit (ELARA-771), reusing {@link resolvePolicyDate} for the offset math. Pure/deterministic — the
 * caller compares `freeUntil` against "now" itself so this stays testable without a clock.
 *
 *  - non-refundable (no refundable policy)       → `{ refundable: false }`
 *  - refundable with a dated deadline            → `{ refundable: true, freeUntil }`  ("free until …")
 *  - refundable with no resolvable deadline      → `{ refundable: true }`             (free, no fixed cutoff)
 *
 * `freeUntil` is a UTC-midnight `Date` (matching the deposit-deadline math); format it in UTC to avoid a
 * day shift. When policies carry several refundable deadlines, the latest (most generous) one wins.
 */
export function resolveCancellationDeadline(input: {
  cancellationPolicies?: CancellationPolicy[];
  checkInDate: string;
  checkOutDate: string;
}): CancellationDeadline {
  const { cancellationPolicies, checkInDate, checkOutDate } = input;
  const refundable = (cancellationPolicies ?? []).filter((p) => p.refundable);
  if (refundable.length === 0) return { refundable: false };

  let freeUntil: Date | undefined;
  for (const policy of refundable) {
    const { dueDate } = resolvePolicyDate({ deadline: policy.deadline, checkInDate, checkOutDate });
    if (dueDate && (!freeUntil || dueDate.getTime() > freeUntil.getTime())) freeUntil = dueDate;
  }
  return { refundable: true, freeUntil };
}

/**
 * Derive a user-facing label for one schedule entry. Pure: depends only on
 * the entry's structural fields. Change wording here without touching stored
 * data.
 */
export function formatPaymentScheduleLabel(entry: PaymentScheduleEntry): string {
  if (entry.kind === 'at_booking') return 'Due today';
  if (entry.dueDate) {
    const date = new Date(`${entry.dueDate}T00:00:00`);
    if (!Number.isNaN(date.getTime())) {
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `Due ${formatter.format(date)}`;
    }
  }
  return 'Due at the property';
}

/**
 * True when any deposit policy is effectively the full amount — a 100% `percent`, or an
 * `amount` (in the matching currency) within 1% of `totalPrice` (the tolerance absorbs
 * rounding between the deposit figure and the displayed total).
 *
 * Such policies are pre-payment requirements dressed up as deposits; callers use this to
 * suppress a redundant "deposit due" line when the "deposit" is just the whole price.
 */
export function isFullAmountDeposit(
  policies: DepositPolicy[],
  totalPrice: number,
  currency: string,
): boolean {
  if (!policies || policies.length === 0) return false;

  return policies.some((policy) => {
    if (!policy?.amountPercent) return false;

    if (policy.amountPercent.percent === 100) return true;

    if (policy.amountPercent.amount && policy.amountPercent.currency === currency) {
      const depositAmount = policy.amountPercent.amount;
      if (!Number.isNaN(depositAmount) && totalPrice > 0) {
        const percentageDiff = Math.abs(depositAmount - totalPrice) / totalPrice;
        if (percentageDiff < 0.01) return true;
      }
    }

    return false;
  });
}

/* ----------------------------- helpers ----------------------------- */

function resolvePolicyAmount(policy: DepositPolicy, total: number): number | undefined {
  const ap = policy.amountPercent;
  if (!ap) return undefined;
  if (ap.amount != null && ap.amount > 0) return ap.amount;
  if (ap.percent != null && ap.percent > 0) return (total * ap.percent) / 100;
  return undefined;
}

interface ResolvedDate {
  dueDate?: Date;
}

function resolvePolicyDate(input: {
  deadline?: { absoluteDeadline?: string; offsetTimeUnit?: string; offsetUnitMultiplier?: number; offsetDropTime?: string };
  checkInDate: string;
  checkOutDate: string;
}): ResolvedDate {
  const { deadline, checkInDate, checkOutDate } = input;
  if (!deadline) return {};

  if (deadline.absoluteDeadline) {
    const parsed = new Date(deadline.absoluteDeadline);
    if (!Number.isNaN(parsed.getTime())) return { dueDate: startOfDay(parsed) };
  }

  const unit = (deadline.offsetTimeUnit ?? '').toLowerCase();
  const mult = deadline.offsetUnitMultiplier ?? 0;
  const drop = deadline.offsetDropTime;

  // AfterBooking / AfterConfirmation collapse to "immediately" for display purposes.
  if (drop === 'AfterBooking' || drop === 'AfterConfirmation') return {};

  const ms = msForUnit(unit, mult);
  if (ms === undefined || drop === undefined) return {};

  const anchor =
    drop === 'BeforeArrival' || drop === 'AfterArrival'
      ? startOfDay(new Date(checkInDate))
      : drop === 'AfterDeparture'
        ? startOfDay(new Date(checkOutDate))
        : undefined;
  if (!anchor || Number.isNaN(anchor.getTime())) return {};

  const due = new Date(anchor.getTime() + (drop === 'BeforeArrival' ? -ms : ms));
  return { dueDate: startOfDay(due) };
}

function msForUnit(unit: string, mult: number): number | undefined {
  const day = 86_400_000;
  switch (unit) {
    case 'day':
    case 'days':
      return mult * day;
    case 'week':
    case 'weeks':
      return mult * day * 7;
    case 'hour':
    case 'hours':
      return mult * (day / 24);
    case 'month':
    case 'months':
      return mult * day * 30;
    default:
      return undefined;
  }
}

// These date helpers work entirely in UTC ON PURPOSE — this is NOT the `parseDateOnlyLocal`
// (local start-of-day) rule from `date-utils.ts`, and the two must not be conflated. Here the
// inputs are date-only `'YYYY-MM-DD'` strings parsed as UTC (`new Date('2026-06-01')`), the offset
// math is in UTC, and the output is another date-only `'YYYY-MM-DD'` string (`toIsoDate` reads
// `getUTC*`). Staying UTC end-to-end makes the computed `dueDate` deterministic across server and
// client regardless of either runtime's time zone — using local time here would make the *output
// date string* depend on where the code ran. (Local start-of-day is the right rule only when
// bucketing against the user's own `now()`, e.g. `isTravelDatePassed`.)
function startOfDay(date: Date): Date {
  const out = new Date(date);
  out.setUTCHours(0, 0, 0, 0);
  return out;
}

function toIsoDate(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function roundCurrency(amount: number): number {
  return Math.round(amount * ROUND_CENTS) / ROUND_CENTS;
}
